import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatCurrency, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { HeartHandshake, Plus, Trash2, FileBarChart, UserPlus } from "lucide-react";

export default function InsurersPage() {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [insurers, setInsurers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [modal, setModal] = useState(false);
  const [reportModal, setReportModal] = useState(false);
  const [report, setReport] = useState(null);
  const [assignModal, setAssignModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [assignForm, setAssignForm] = useState({ customer_id: "", policy_number: "" });
  const [form, setForm] = useState({ name: "", nif: "", contact_phone: "", email: "", coverage_pct: "100" });

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const [i, c] = await Promise.all([
        axios.get(`${API_URL}/insurers`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/customers`, { headers, withCredentials: true }),
      ]);
      setInsurers(i.data); setCustomers(c.data);
    } catch { toast.error("Erro ao carregar seguradoras"); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/insurers`, { ...form, coverage_pct: parseFloat(form.coverage_pct) || 100 }, { headers, withCredentials: true });
      toast.success("Seguradora criada");
      setModal(false); setForm({ name: "", nif: "", contact_phone: "", email: "", coverage_pct: "100" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const remove = async (ins) => {
    if (!window.confirm(`Remover seguradora "${ins.name}"? Os clientes associados serão desassociados.`)) return;
    await axios.delete(`${API_URL}/insurers/${ins.insurer_id}`, { headers, withCredentials: true });
    fetchAll();
  };

  const openReport = async (ins) => {
    try {
      const res = await axios.get(`${API_URL}/insurers/${ins.insurer_id}/report`, { headers, withCredentials: true });
      setReport(res.data);
      setReportModal(true);
    } catch { toast.error("Erro ao gerar relatório"); }
  };

  const assign = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.put(`${API_URL}/customers/${assignForm.customer_id}/insurer`, {
        insurer_id: selected.insurer_id,
        policy_number: assignForm.policy_number || null,
      }, { headers, withCredentials: true });
      toast.success(res.data.message);
      setAssignModal(false); setAssignForm({ customer_id: "", policy_number: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-6" data-testid="insurers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Seguradoras & Convénios</h1>
          <p className="text-muted-foreground">Associe clientes a seguradoras e acompanhe a faturação por convénio</p>
        </div>
        <Button onClick={() => setModal(true)} data-testid="add-insurer-btn"><Plus className="w-4 h-4 mr-2" /> Nova Seguradora</Button>
      </div>

      {insurers.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><HeartHandshake className="w-10 h-10 mx-auto mb-3 opacity-40" />Nenhuma seguradora registada</CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {insurers.map((ins) => (
            <Card key={ins.insurer_id} data-testid={`insurer-${ins.insurer_id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold">{ins.name}</p>
                    <p className="text-xs text-muted-foreground">{ins.nif ? `NIF ${ins.nif} · ` : ""}{ins.contact_phone || ""}</p>
                  </div>
                  <Badge className="bg-blue-500/20 text-blue-500">{ins.coverage_pct}% cobertura</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{ins.customers_count || 0} cliente(s) associado(s)</p>
                <div className="flex gap-2 flex-wrap pt-1">
                  <Button size="sm" variant="outline" onClick={() => { setSelected(ins); setAssignModal(true); }} data-testid={`assign-insurer-${ins.insurer_id}`}>
                    <UserPlus className="w-4 h-4 mr-1" /> Associar Cliente
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openReport(ins)} data-testid={`report-insurer-${ins.insurer_id}`}>
                    <FileBarChart className="w-4 h-4 mr-1" /> Relatório
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(ins)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nova Seguradora / Convénio</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div className="space-y-1"><Label>Nome *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="insurer-name-input" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label>NIF</Label><Input value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} /></div>
              <div className="space-y-1"><Label>Cobertura (%)</Label><Input type="number" min="0" max="100" value={form.coverage_pct} onChange={(e) => setForm({ ...form, coverage_pct: e.target.value })} data-testid="insurer-coverage-input" /></div>
            </div>
            <div className="space-y-1"><Label>Telefone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
            <div className="space-y-1"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-insurer-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={assignModal} onOpenChange={setAssignModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Associar Cliente — {selected?.name}</DialogTitle></DialogHeader>
          <form onSubmit={assign} className="space-y-3">
            <div className="space-y-1">
              <Label>Cliente *</Label>
              <Select value={assignForm.customer_id} onValueChange={(v) => setAssignForm({ ...assignForm, customer_id: v })}>
                <SelectTrigger data-testid="assign-customer-select"><SelectValue placeholder="Escolher cliente" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>{c.name}{c.insurer_name ? ` (${c.insurer_name})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Nº Apólice / Cartão</Label><Input value={assignForm.policy_number} onChange={(e) => setAssignForm({ ...assignForm, policy_number: e.target.value })} data-testid="policy-number-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAssignModal(false)}>Cancelar</Button>
              <Button type="submit" disabled={!assignForm.customer_id} data-testid="confirm-assign-btn">Associar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={reportModal} onOpenChange={setReportModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Relatório — {report?.insurer?.name}</DialogTitle></DialogHeader>
          {report && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Total Faturado</p><p className="font-mono font-bold">{formatCurrency(report.total_billed)}</p></div>
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Parte Seguradora</p><p className="font-mono font-bold text-blue-500">{formatCurrency(report.insurer_share)}</p></div>
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Parte Cliente</p><p className="font-mono font-bold">{formatCurrency(report.customer_share)}</p></div>
              </div>
              <p className="text-sm font-semibold">Clientes associados ({report.customers.length})</p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {report.customers.map((c) => (
                  <div key={c.customer_id} className="flex justify-between text-sm border-b pb-1">
                    <span>{c.name}</span><span className="text-xs text-muted-foreground">{c.policy_number || "-"}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm font-semibold">Faturas ({report.invoices_count})</p>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {report.invoices.map((inv, i) => (
                  <div key={i} className="flex justify-between text-sm border-b pb-1">
                    <span className="font-mono">{inv.invoice_number}</span>
                    <span>{inv.customer_name}</span>
                    <span className="font-mono">{formatCurrency(inv.total)}</span>
                  </div>
                ))}
                {report.invoices.length === 0 && <p className="text-sm text-muted-foreground">Sem faturas</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
